import { useState, useContext, useRef, useEffect } from "react";
import axios from "axios";
import { OperaContext } from "../../context/OperaContext";
import ImageModal from "../../components/ImagePreviewModal";
import useGoogleDriveImagePicker from "../../components/useGoogleDriveImagePicker";
import ImagePreview from "../../components/imagePreview";
import { toast } from "react-toastify";

function CityTab() {
  const {
    allCities = [],
    setAllCities = () => { },
    fetchData,
  } = useContext(OperaContext);
  const [newCity, setNewCity] = useState({
    name: "",
    image: null,
  });
  const [editingCity, setEditingCity] = useState(null);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const cityFormRef = useRef(null);
  const formContainerRef = useRef(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalImage, setModalImage] = useState("");

  const openModal = (image) => {
    setModalImage(image);
    setModalOpen(true);
  };

  const sortedVisibleCities = [...allCities]
    ?.sort((a, b) => a.name?.localeCompare(b.name))
    .filter((city) =>
      city.name?.toLowerCase().includes(searchTerm.toLowerCase())
    );

  const handleCityChange = (e) => {
    const { name, value } = e.target;
    setNewCity((prev) => ({ ...prev, [name]: value }));
    if (editingCity)
      setEditingCity((prev) => ({ ...prev, [name]: value }));
  };

  const handleCityFileChange = (e) => {
    const file = e.target.files[0];

    if (editingCity)
      setEditingCity((prev) => ({ ...prev, image: file }));
    if (!editingCity) setNewCity((prev) => ({ ...prev, image: file }));
  };


  const resetForm = () => {
    setNewCity({
      name: "",
      image: null,
    });
    if (cityFormRef.current) {
      cityFormRef.current.reset();
    }
  };

  const submitCity = async (formData, isEdit = false, id = null) => {
    try {
      const url = isEdit ? `/api/cities/${id}` : "/api/cities";
      const method = isEdit ? "put" : "post";
      const res = await axios({
        method,
        url,
        data: formData,
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 15000,
      });
      if (res.status === 200 || res.status === 201) {
        toast.success(`${isEdit ? "Updated" : "Added"} city Successfully`);
      }
      return res.data;
    } catch (err) {
      const errorMsg =
        err.response?.data?.message ||
        `Failed to ${isEdit ? "update" : "add"} city`;
      toast.error(errorMsg);
    }
  };

  const handleCitySubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData();
    formData.append("name", newCity.name);
    if (editingCity) formData.append("image", editingCity.image);
    if (!editingCity) formData.append("image", newCity.image);

    try {
      setError(null);
      const result = await submitCity(
        formData,
        !!editingCity,
        editingCity?._id
      );

      // Update local state immediately for better UX
      if (editingCity) {
        setAllCities((prev) =>
          prev.map((c) => (c._id === editingCity._id ? result : c))
        );
      } else {
        setAllCities((prev) => [...prev, result]);
      }

      setEditingCity(null);
      resetForm();
      fetchData();
    } catch (err) {
      setError(err.message);
    }
  };

  const cancelEditing = () => {
    setEditingCity(null);
    resetForm();
  };

  const editCity = (city) => {
    setEditingCity(city);
    setNewCity({
      name: city.name,
      image: city.image,
    });
    setSearchTerm("");
  };

  const deleteCity = async (
    composerId
  ) => {
    if (!window.confirm("Are you sure you want to delete this city?"))
      return;
    try {
      await axios.delete(`/api/cities/${composerId}`, { timeout: 15000 });
      console.log("Deleted city:", composerId);
      setAllCities((prev) => prev.filter((c) => c._id !== composerId));
      setEditingCity(null);
      resetForm();
      fetchData();
      setError(null);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to delete city");
    }
  };

  useEffect(() => {
    if (editingCity && formContainerRef.current) {
      // Use setTimeout to ensure the DOM has updated
      setTimeout(() => {
        const yOffset = -70; // Add extra offset to ensure form is fully visible
        const y =
          formContainerRef.current.getBoundingClientRect().top +
          window.pageYOffset +
          yOffset;
        window.scrollTo({ top: y, behavior: "smooth" });
      }, 100);
    }
  }, [editingCity]);
  const handleOpenPicker = useGoogleDriveImagePicker({
    isEditing: editingCity ? true : false,
    setFormData: setNewCity,
    setFormDataEdit: setEditingCity,
    setError,
  });
  return (
    <div className="max-w-[1280px] mx-auto p-4 md:p-8 bg-[#1a1a1a] rounded-lg text-white flex flex-col gap-4">
      <ImageModal
        open={modalOpen}
        image={modalImage}
        onClose={() => setModalOpen(false)}
      />
      <div ref={formContainerRef} id="city-form-container">
        <h2 className="text-xl text-[#d4a017] font-cinzel uppercase font-normal text-center md:text-left mb-4">
          {editingCity ? "Edit City" : "Add New City"}
        </h2>
        {error && <p className="text-[#f44336] text-center mb-4">{error}</p>}
        <form
          onSubmit={handleCitySubmit}
          ref={cityFormRef}
          className="flex flex-col gap-4 mb-8"
          id="city-form"
        >
          <input
            type="text"
            name="name"
            value={newCity.name}
            onChange={handleCityChange}
            placeholder="City Name"
            className="p-2 bg-[#333] border border-[#555] rounded text-white text-base"
            required
          />
          {(editingCity?.image || newCity.image) && (
            <ImagePreview
              image={editingCity?.image || newCity.image}
              onClick={openModal}
            />
          )}
          <div className="flex gap-2">
            <div className="relative">
              <input
                type="file"
                id="city-image-file"
                name="image"
                onChange={handleCityFileChange}
                accept="image/*"
                className="pl-10 pr-3 py-2 bg-[#333] border border-[#555] rounded text-white"
              />
              <label
                htmlFor="city-image-file"
                className="absolute left-3 top-1/2 transform -translate-y-1/2 cursor-pointer pointer-events-none"
              >
                <img
                  src="/images/attach.svg"
                  alt="Choose File"
                  className="w-4 h-4 filter invert"
                />
              </label>
            </div>
            <button
              type="button"
              onClick={() => handleOpenPicker("image")}
              className="px-3 py-2 bg-[#666] text-white rounded-md hover:bg-[#555] transition-colors w-fit flex items-center gap-2"
            >
              <img
                src="/images/drive.png"
                alt="Google Drive"
                className="w-4 h-4"
              />
              Google Drive
            </button>
          </div>
          <div className="flex gap-2">
            <button
              type="submit"
              className="px-4 py-2 bg-[#d4a017] hover:bg-[#b38b00] text-white rounded transition-colors cursor-pointer"
            >
              {editingCity ? "Update City" : "Add City"}
            </button>
            {editingCity && (
              <>
                <button
                  type="button"
                  onClick={cancelEditing}
                  className="px-4 py-2 bg-[#666] hover:bg-[#555] text-white rounded transition-colors cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() =>
                    deleteCity(editingCity._id, editingCity.hidden)
                  }
                  className="px-4 py-2 bg-[#f44336] hover:bg-[#da190b] text-white rounded transition-colors cursor-pointer"
                >
                  Delete
                </button>
              </>
            )}
          </div>
        </form>
      </div>

      {/* Search bar */}
      <div className="mb-4">
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search cities..."
          className="p-2 bg-[#333] border border-[#555] rounded text-white text-base w-full"
        />
      </div>

      <h3 className="text-lg text-[#d4a017] font-cinzel uppercase font-normal text-center md:text-left mb-4">
        Cities
      </h3>
      <div>
        <ul className="list-none p-0">
          {sortedVisibleCities.length === 0 ? (
            <p className="text-[#ccc] text-center">
              {searchTerm
                ? "No visible cities match your search."
                : "No visible cities available."}
            </p>
          ) : (
            sortedVisibleCities.map((city) => (
              <li
                key={city._id}
                className="flex flex-col md:flex-row justify-between items-center py-2 border-b border-[#444]"
              >
                <span className="text-white flex-1 text-center md:text-left">
                  {city.name}
                </span>
                <div className="flex flex-row gap-2 mt-2 mr-2 md:mt-0">
                  <button
                    onClick={() => editCity(city)}
                    className="px-3 py-1 bg-[#4CAF50] hover:bg-[#45a049] text-white text-sm rounded transition-colors cursor-pointer"
                  >
                    Edit
                  </button>
                </div>
              </li>
            ))
          )}
        </ul>
      </div>
    </div>
  );
}

export default CityTab;
